import React, { useState, useEffect, useMemo } from 'react';
import { Filter, Download, RefreshCw, SlidersHorizontal, X } from 'lucide-react';
import type { DashboardFilters } from '../../types/dashboard.types';
import { WorkStage, CategoryPST } from '../../types/work.types';

interface DistrictOption { id: number; name: string; }
interface MandalOption { mandal_id: number; mandal_name: string; }

interface FilterBarProps {
  filters: DashboardFilters;
  onFiltersChange: (filters: DashboardFilters) => void;
  onExport?: () => void;
  onRefresh?: () => void;
  loading?: boolean;
  showSchemeType?: boolean;
  exporting?: boolean;
}

const GRANT_OPTIONS = ['All', 'CRR', 'MRR', 'SDF', 'FDR', 'PMGSY', 'MGNREGS', 'DMFT', 'IMS'];
const SCHEME_TYPE_OPTIONS = ['All', 'State', 'Central', 'District'];

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFiltersChange,
  onExport,
  onRefresh,
  loading = false,
  showSchemeType = false,
  exporting = false,
}) => {
  const [districts, setDistricts] = useState<DistrictOption[]>([]);
  const [mandals, setMandals] = useState<MandalOption[]>([]);
  const [workTypes, setWorkTypes] = useState<string[]>([]);
  const [subStages, setSubStages] = useState<string[]>([]);
  const [contractors, setContractors] = useState<{ id: number; name: string }[]>([]);
  const [constituencies, setConstituencies] = useState<{ id: number; name: string }[]>([]);
  const [territories, setTerritories] = useState<{ id: number; name: string }[]>([]);
  const [circles, setCircles] = useState<{ id: number; name: string }[]>([]);
  const [divisions, setDivisions] = useState<{ id: number; name: string; circle_id: number }[]>([]);
  const [subDivisions, setSubDivisions] = useState<{ id: number; name: string }[]>([]);
  const [hierMandals, setHierMandals] = useState<{ id: number; name: string }[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';
  const authGet = async (path: string) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${baseUrl}${path}`, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
    if (!res.ok) return null;
    return res.json();
  };
  const unwrap = (j: any): any[] => (Array.isArray(j) ? j : j?.data ?? j?.items ?? []);

  useEffect(() => {
    authGet('/master-data/districts/stats').then((j) => {
      setDistricts(unwrap(j).map((d: any) => ({ id: d.district_id || d.id, name: d.district_name || d.name })));
    }).catch(() => setDistricts([]));
    authGet('/master-data/work-types').then((j) => {
      setWorkTypes(unwrap(j).map((w: any) => w.work_type_name).filter(Boolean));
    }).catch(() => setWorkTypes([]));
    authGet('/work-stages/level/SUB').then((j) => {
      setSubStages(unwrap(j).map((s: any) => s.name).filter(Boolean));
    }).catch(() => setSubStages([]));
    authGet('/master-data/contractors').then((j) => {
      setContractors(unwrap(j).map((c: any) => ({ id: c.contractor_id || c.id, name: c.contractor_name || c.firm_name || c.name })));
    }).catch(() => setContractors([]));
    authGet('/constituencies').then((j) => {
      setConstituencies(unwrap(j).map((c: any) => ({ id: c.id, name: c.name })));
    }).catch(() => setConstituencies([]));
    authGet('/ce-territories/active').then((j) => {
      setTerritories(unwrap(j).map((t: any) => ({ id: t.id, name: t.name })));
    }).catch(() => setTerritories([]));
    authGet('/divisions?limit=100').then((j) => {
      setDivisions(unwrap(j).map((d: any) => ({ id: d.id, name: d.name, circle_id: Number(d.circle_id) })));
    }).catch(() => setDivisions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  // Circles load for the selected territory; divisions are filtered by circle.
  useEffect(() => {
    if (!filters.territoryId) { setCircles([]); return; }
    authGet(`/circles/ce-territory/${filters.territoryId}`)
      .then((j) => setCircles(unwrap(j).map((c: any) => ({ id: c.id, name: c.name }))))
      .catch(() => setCircles([]));
  }, [filters.territoryId, baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const divisionOptions = useMemo(
    () => (filters.circleId ? divisions.filter((d) => d.circle_id === Number(filters.circleId)) : []),
    [divisions, filters.circleId],
  );

  // Sub-divisions load for the selected division; mandals for the selected sub-division.
  useEffect(() => {
    if (!filters.divisionId) { setSubDivisions([]); return; }
    authGet(`/sub-divisions/division/${filters.divisionId}`)
      .then((j) => setSubDivisions(unwrap(j).map((s: any) => ({ id: s.id, name: s.name }))))
      .catch(() => setSubDivisions([]));
  }, [filters.divisionId, baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!filters.subDivisionId) { setHierMandals([]); return; }
    authGet(`/mandals/sub-division/${filters.subDivisionId}`)
      .then((j) => setHierMandals(unwrap(j).map((m: any) => ({ id: m.id, name: m.name }))))
      .catch(() => setHierMandals([]));
  }, [filters.subDivisionId, baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!filters.districtId) { setMandals([]); return; }
    authGet(`/dashboard/mandal-wise-summary?districtId=${filters.districtId}`)
      .then((j) => setMandals(Array.isArray(j) ? j : [])).catch(() => setMandals([]));
  }, [filters.districtId, baseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (patch: Partial<DashboardFilters>) => {
    const next = { ...filters, ...patch };
    if (patch.districtId !== undefined && patch.districtId !== filters.districtId) next.mandalId = undefined;
    if ('territoryId' in patch) { next.circleId = undefined; next.divisionId = undefined; next.subDivisionId = undefined; next.mandalId = undefined; }
    if ('circleId' in patch) { next.divisionId = undefined; next.subDivisionId = undefined; next.mandalId = undefined; }
    if ('divisionId' in patch) { next.subDivisionId = undefined; next.mandalId = undefined; }
    if ('subDivisionId' in patch) next.mandalId = undefined;
    onFiltersChange(next);
  };
  const clearAll = () => onFiltersChange({ grant: 'All' });

  const activeCount = Object.entries(filters).filter(
    ([k, v]) => k !== 'grant' && v !== undefined && v !== '' && v !== null,
  ).length + (filters.grant && filters.grant !== 'All' ? 1 : 0);

  const sel = 'border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-gray-600">
          <Filter className="h-4 w-4" />
          {activeCount > 0 && <span className="bg-blue-100 text-blue-700 text-xs font-semibold rounded-full px-2 py-0.5">{activeCount}</span>}
        </div>

        {showSchemeType && (
          <select className={sel} value={filters.schemeType || 'All'}
            onChange={(e) => update({ schemeType: e.target.value === 'All' ? undefined : e.target.value })}>
            {SCHEME_TYPE_OPTIONS.map((s) => <option key={s} value={s}>{s === 'All' ? 'All Scheme Types' : s}</option>)}
          </select>
        )}

        <select className={sel} value={filters.grant} onChange={(e) => update({ grant: e.target.value })}>
          {GRANT_OPTIONS.map((g) => <option key={g} value={g}>{g === 'All' ? 'All Grants' : g}</option>)}
        </select>

        <select className={sel} value={filters.districtId || ''}
          onChange={(e) => update({ districtId: e.target.value ? Number(e.target.value) : undefined })}>
          <option value="">All Districts</option>
          {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>

        {filters.districtId && mandals.length > 0 && (
          <select className={sel} value={filters.mandalId || ''}
            onChange={(e) => update({ mandalId: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">All Mandals</option>
            {mandals.map((m) => <option key={m.mandal_id} value={m.mandal_id}>{m.mandal_name}</option>)}
          </select>
        )}

        <button onClick={() => setShowAdvanced((s) => !s)}
          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          <SlidersHorizontal className="h-4 w-4 mr-1" />
          {showAdvanced ? 'Less' : 'More filters'}
        </button>
        {activeCount > 0 && (
          <button onClick={clearAll}
            className="inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 hover:text-red-600">
            <X className="h-4 w-4 mr-1" /> Clear
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          {onRefresh && (
            <button onClick={onRefresh} disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          )}
          {onExport && (
            <button onClick={onExport} disabled={exporting || loading}
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              <Download className="h-4 w-4 mr-1" /> {exporting ? 'Exporting...' : 'Export Excel'}
            </button>
          )}
        </div>
      </div>

      {showAdvanced && (
        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-3">
          <select className={sel} value={filters.territoryId || ''}
            onChange={(e) => update({ territoryId: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">All Territories</option>
            {territories.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <select className={sel} value={filters.circleId || ''} disabled={!filters.territoryId}
            onChange={(e) => update({ circleId: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">{filters.territoryId ? 'All Circles' : 'Select territory first'}</option>
            {circles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className={sel} value={filters.divisionId || ''} disabled={!filters.circleId}
            onChange={(e) => update({ divisionId: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">{filters.circleId ? 'All Divisions' : 'Select circle first'}</option>
            {divisionOptions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select className={sel} value={filters.subDivisionId || ''} disabled={!filters.divisionId}
            onChange={(e) => update({ subDivisionId: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">{filters.divisionId ? 'All Sub-divisions' : 'Select division first'}</option>
            {subDivisions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className={sel} value={(filters.subDivisionId && filters.mandalId) || ''} disabled={!filters.subDivisionId}
            onChange={(e) => update({ mandalId: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">{filters.subDivisionId ? 'All Mandals' : 'Select sub-division first'}</option>
            {hierMandals.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select className={sel} value={filters.stage || ''} onChange={(e) => update({ stage: e.target.value || undefined })}>
            <option value="">All Work Stages</option>
            {Object.values(WorkStage).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className={sel} value={filters.subStage || ''} onChange={(e) => update({ subStage: e.target.value || undefined })}>
            <option value="">All Sub-Stages</option>
            {subStages.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className={sel} value={filters.category || ''} onChange={(e) => update({ category: e.target.value || undefined })}>
            <option value="">All Categories (PST)</option>
            {Object.values(CategoryPST).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className={sel} value={filters.workType || ''} onChange={(e) => update({ workType: e.target.value || undefined })}>
            <option value="">All Work Types</option>
            {workTypes.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          <select className={sel} value={filters.contractorId || ''} onChange={(e) => update({ contractorId: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">All Contractors</option>
            {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className={sel} value={filters.constituencyId || ''} onChange={(e) => update({ constituencyId: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">All Constituencies</option>
            {constituencies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="text" placeholder="Agency name" className={sel} value={filters.agencyName || ''}
            onChange={(e) => update({ agencyName: e.target.value || undefined })} />
          <input type="number" placeholder="Min cost" className={sel} value={filters.minCost ?? ''}
            onChange={(e) => update({ minCost: e.target.value ? Number(e.target.value) : undefined })} />
          <input type="number" placeholder="Max cost" className={sel} value={filters.maxCost ?? ''}
            onChange={(e) => update({ maxCost: e.target.value ? Number(e.target.value) : undefined })} />
          <label className="text-xs text-gray-500">Sanction from
            <input type="date" className={`${sel} w-full`} value={filters.fromDate || ''}
              onChange={(e) => update({ fromDate: e.target.value || undefined })} /></label>
          <label className="text-xs text-gray-500">Sanction to
            <input type="date" className={`${sel} w-full`} value={filters.toDate || ''}
              onChange={(e) => update({ toDate: e.target.value || undefined })} /></label>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
